import React, { useState } from "react";
import {
  AppShell,
  Container,
  Title,
  Text,
  Paper,
  Button,
  Stack,
  Group,
  Code,
  Alert,
  Divider,
  Badge,
} from "@mantine/core";
import { Header } from "../components/layout/Header";
import { useAuth } from "../auth/AuthProvider";
import { getRoleName, UserRole } from "../models/user.model";
import {
  AdminOnly,
  ManagerOrAdmin,
  RoleBasedAccess,
} from "../components/common/RoleBasedAccess";
import {
  ExclamationTriangleIcon,
  LockClosedIcon,
} from "@heroicons/react/24/outline";
import { useCurrentUser } from "../hooks/queries/useCurrentUser";

export function RoleDemoPage() {
  const { userRole, currentUser, updateUserRole } = useAuth();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const {
    data: reactQueryUser,
    isLoading: userLoading,
    refetch: refetchUser,
  } = useCurrentUser();

  const handleRefreshUser = async () => {
    setIsRefreshing(true);
    try {
      const { data: userData } = await refetchUser();
      if (userData) {
        console.log("User role refreshed:", userData);
        updateUserRole(userData);
      }
    } catch (err) {
      console.error("Error refreshing user data:", err);
    } finally {
      setIsRefreshing(false);
    }
  };

  const getRoleBadgeColor = (role: UserRole): string => {
    switch (role) {
      case UserRole.Admin:
        return "red";
      case UserRole.Manager:
        return "blue";
      case UserRole.User:
        return "green";
      default:
        return "gray";
    }
  };

  return (
    <AppShell header={{ height: 60 }} padding="md">
      <AppShell.Header>
        <Header />
      </AppShell.Header>
      <AppShell.Main>
        <Container size="md" py="xl">
          <Title order={2} mb="lg">
            Role-Based Access Demo
          </Title>

          <Paper p="xl" withBorder mb="lg">
            <Group mb="md">
              <Text fw={700}>Your Current Role:</Text>
              {userRole ? (
                <Badge size="lg" color={getRoleBadgeColor(userRole)}>
                  {getRoleName(userRole)}
                </Badge>
              ) : (
                <Text>Not authenticated or role not determined</Text>
              )}
            </Group>

            <Text mb="md">
              This page demonstrates how different UI elements and functionality
              can be restricted based on user roles.
            </Text>

            <Group>
              <Button onClick={handleRefreshUser} loading={isRefreshing}>
                Refresh User Data
              </Button>
            </Group>
          </Paper>

          <Divider
            my="xl"
            label="Role-Based UI Components"
            labelPosition="center"
          />

          <Stack gap="lg">
            {/* Section visible to all users */}
            <Paper p="lg" withBorder>
              <Title order={3} mb="md">
                All Users Section
              </Title>
              <Text>
                This section is visible to all authenticated users regardless of
                role.
              </Text>
            </Paper>

            {/* Admin only section */}
            <AdminOnly
              fallback={
                <Paper
                  p="lg"
                  withBorder
                  style={{ borderColor: "var(--mantine-color-red-6)" }}
                >
                  <Group mb="sm">
                    <LockClosedIcon
                      width={24}
                      color="var(--mantine-color-red-6)"
                    />
                    <Text fw={700} c="red">
                      Admin Access Required
                    </Text>
                  </Group>
                  <Text>You need Admin privileges to see this content.</Text>
                </Paper>
              }
            >
              <Paper
                p="lg"
                withBorder
                style={{ borderColor: "var(--mantine-color-red-6)" }}
              >
                <Title order={3} mb="md" c="red">
                  Admin Only Section
                </Title>
                <Text>
                  This section is only visible to users with the Admin role.
                </Text>
                <Alert my="md" color="red" title="Sensitive Configuration">
                  <Text>
                    Here you can manage system configuration, users, and other
                    administrative tasks.
                  </Text>
                </Alert>
                <Button color="red">Admin Action</Button>
              </Paper>
            </AdminOnly>

            {/* Manager or Admin section */}
            <ManagerOrAdmin
              fallback={
                <Paper
                  p="lg"
                  withBorder
                  style={{ borderColor: "var(--mantine-color-blue-6)" }}
                >
                  <Group mb="sm">
                    <LockClosedIcon
                      width={24}
                      color="var(--mantine-color-blue-6)"
                    />
                    <Text fw={700} c="blue">
                      Manager or Admin Access Required
                    </Text>
                  </Group>
                  <Text>
                    You need Manager or Admin privileges to see this content.
                  </Text>
                </Paper>
              }
            >
              <Paper
                p="lg"
                withBorder
                style={{ borderColor: "var(--mantine-color-blue-6)" }}
              >
                <Title order={3} mb="md" c="blue">
                  Manager/Admin Section
                </Title>
                <Text>
                  This section is visible to users with either Manager or Admin
                  roles.
                </Text>
                <Alert my="md" color="blue" title="Team Management">
                  <Text>
                    Here you can manage team members, assignments, and reports.
                  </Text>
                </Alert>
                <Button color="blue">Management Action</Button>
              </Paper>
            </ManagerOrAdmin>

            {/* User specific section */}
            <RoleBasedAccess
              allowedRoles={[UserRole.User]}
              fallback={
                <Paper
                  p="lg"
                  withBorder
                  style={{ borderColor: "var(--mantine-color-green-6)" }}
                >
                  <Group mb="sm">
                    <ExclamationTriangleIcon
                      width={24}
                      color="var(--mantine-color-green-6)"
                    />
                    <Text fw={700} c="green">
                      Note for Admins/Managers
                    </Text>
                  </Group>
                  <Text>This section is only visible to regular users.</Text>
                  <Text size="sm" c="dimmed" mt="sm">
                    As an Admin or Manager, you're seeing this message instead
                    of the user content.
                  </Text>
                </Paper>
              }
            >
              <Paper
                p="lg"
                withBorder
                style={{ borderColor: "var(--mantine-color-green-6)" }}
              >
                <Title order={3} mb="md" c="green">
                  Regular User Section
                </Title>
                <Text>
                  This section is only visible to regular users (not Admins or
                  Managers).
                </Text>
                <Alert my="md" color="green" title="User Tips">
                  <Text>
                    Here are some helpful tips for using the system as a regular
                    user.
                  </Text>
                </Alert>
                <Button color="green">User Action</Button>
              </Paper>
            </RoleBasedAccess>
          </Stack>

          <Divider my="xl" label="Implementation Code" labelPosition="center" />

          <Paper p="lg" withBorder>
            <Title order={3} mb="md">
              How to Use Role-Based Components
            </Title>
            <Text mb="md">
              You can use these components throughout your application to
              conditionally render UI elements based on user roles:
            </Text>

            <Code block mb="md">{`
// For admin-only content:
<AdminOnly>
  <RestrictedComponent />
</AdminOnly>

// For manager or admin content:
<ManagerOrAdmin>
  <ManagementComponent />
</ManagerOrAdmin>

// For custom role combinations:
<RoleBasedAccess allowedRoles={[UserRole.Admin, UserRole.Manager]}>
  <CustomComponent />
</RoleBasedAccess>
            `}</Code>

            <Text>
              These components will automatically check the user's role from the
              Auth context and render accordingly.
            </Text>
          </Paper>
        </Container>
      </AppShell.Main>
    </AppShell>
  );
}
